import { MinimalSidebarV2 } from "@component/mini-sidebar_v2";
import AdminSidebar from "@component/admin-sidebar";
import { useUserProfileContext } from "@context/user_profile";
import { NavItem } from "@type/navItems";
import { getCookie, setCookie } from "@util/cookie";
import { cn } from "@util/tailwind";

const ADMIN_ROLES = ["admin", "super_admin"];
import { BarChart3Icon, BriefcaseIcon, Building, CheckIcon, ClipboardCheckIcon, CreditCardIcon, FileIcon, FileTextIcon, Globe, HomeIcon, LayoutGridIcon, LifeBuoyIcon, MailIcon, MessageSquareIcon, PackageIcon, Video, Wallet2Icon , UsersIcon , Building2Icon, WandSparklesIcon, WrenchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NotificationDot from "@component/notification-dot";
import { Header } from "@component/header";
import { isOuraEnabled } from "@oura/role";
import { DOCS_WRITE_ON } from "@oura/docs/writeFlag";
import MaintenanceBanner from "@container/maintenance-banner";
import { SnackbarProvider } from "@hook/use-snackbar";
import { Tooltip } from "@component/tooltip";
import TrialUsageBanner from "@component/trial-usage-banner";

interface Props {
    isBeta?: boolean;
    children: React.ReactNode;
    isDisabledFlex?: boolean;
}

export function DashboardLayout(props: Props) {
    const isAccountStartupScreening: boolean = Boolean((getCookie("__account_startup_screening__")));
    const [_, userInfo] = useUserProfileContext();
    const [navItems, setNavItems] = useState<NavItem[]>([])
    const [isShowDotResume, setShowDotResume] = useState<boolean>(!getCookie("__dot_resume_menu__"))
    const navigate = useNavigate();
    const currentPath = window.location.pathname;
    const currentPage = navItems.find((item) => item.to === currentPath)?.name;

    // Admin / super-admin get the labelled collapsible sidebar (others keep the rail).
    const isAdmin = ADMIN_ROLES.includes(userInfo?.role || "");
    const [sidebarMini, setSidebarMini] = useState<boolean>(getCookie("__admin_sidebar_mini__") === "true");
    const toggleSidebar = () => {
        const next = !sidebarMini;
        setSidebarMini(next);
        setCookie("__admin_sidebar_mini__", String(next));
    };

    useEffect(() => {
        if (userInfo) {
            if (userInfo.role === "hr" || userInfo.role === "admin" || userInfo.role === "super_admin") {
                setNavItems([
                    // ── Recruitment ──
                    {
                        name: "dashboard",
                        tooltip: "Browse candidate profiles",
                        icon: HomeIcon,
                        label: "Dashboard",
                        to: "/dashboard",
                        roles: ["hr", "admin", "super_admin"],
                        group: "recruitment"
                    },
                    {
                        name: "jobs",
                        icon: BriefcaseIcon,
                        tooltip: "Manage job listings and applicants",
                        label: "Jobs",
                        to: "/jobs",
                        roles: ["hr", "admin", "super_admin"],
                        group: "recruitment"
                    },
                    {
                        name: "assessments",
                        icon: ClipboardCheckIcon,
                        tooltip: "Manage forms and AI interviews",
                        label: "Screen",
                        to: "/assess",
                        roles: ["hr", "admin", "super_admin"],
                        group: "recruitment"
                    },
                    // ── Billing (HR) ──
                    {
                        name: "billing",
                        icon: Wallet2Icon,
                        tooltip: "Manage usage and invoices",
                        label: "Billing",
                        to: "/billing",
                        roles: ["hr"],
                        group: "billing"
                    },
                    // "Subscription" menu intentionally hidden on all platforms —
                    // its surfaces (Payment Details / Plans + Workspace Activity)
                    // now live under the Billing menu. The /subscription route is
                    // still reachable by direct URL.
                    // ── Administration (admin / super admin) ──
                    {
                        name: "workspaces",
                        icon: LayoutGridIcon,
                        tooltip: "Manage company workspaces",
                        label: "Workspaces",
                        to: "/workspaces",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "admin-users",
                        icon: UsersIcon,
                        tooltip: "Look up any user account across workspaces",
                        label: "Users",
                        to: "/admin/users",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "subscription-packages",
                        icon: PackageIcon,
                        tooltip: "Manage subscription packages",
                        label: "Subscription Packages",
                        to: "/admin/subscription-packages",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "hiring-activity",
                        icon: BarChart3Icon,
                        tooltip: "Cross-company hiring oversight",
                        label: "Hiring Activity",
                        to: "/hiring-activity",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "candidate-investigator",
                        icon: LifeBuoyIcon,
                        tooltip: "Investigate candidate screening issues",
                        label: "Investigator",
                        to: "/candidate-investigator",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "maya-chat-simulator",
                        icon: MessageSquareIcon,
                        tooltip: "Test Maya's replies without a real WhatsApp number",
                        label: "Maya Chat Simulator",
                        to: "/maya-chat-simulator",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    {
                        name: "maintenance",
                        icon: WrenchIcon,
                        tooltip: "Maintenance mode and customer notices",
                        label: "Maintenance",
                        to: "/admin/maintenance",
                        roles: ["admin", "super_admin"],
                        group: "administration"
                    },
                    // ── Tools ──
                    {
                        name: "demo",
                        icon: Building,
                        tooltip: "Create a demo workspace",
                        label: "Demo",
                        to: "/demo",
                        roles: ["admin", "super_admin"],
                        group: "tools"
                    },
                    {
                        name: "scheduler-test",
                        icon: MailIcon,
                        tooltip: "Simulate scheduled emails",
                        label: "Scheduler Test",
                        to: "/scheduler-test",
                        roles: ["admin", "super_admin"],
                        group: "tools"
                    },
                    // ── Growth (Oura) ──
                    // Hidden entirely when the product is not launched here, the
                    // same test `OuraRoute` gates on. A menu entry that leads to
                    // /unauthorized is worse than no entry: it advertises a
                    // product and then refuses it.
                    ...(isOuraEnabled()
                        ? [{
                            name: "oura-businesses",
                            icon: Building2Icon,
                            tooltip: "AI visibility for local businesses",
                            label: "Businesses",
                            to: "/oura/businesses",
                            roles: ["admin", "super_admin"],
                            group: "growth",
                        },
                        // The Skill library (Oura Docs phase 2): only where the
                        // Docs write switch is on, as its route is.
                        ...(DOCS_WRITE_ON
                            ? [{
                                name: "oura-skills",
                                icon: WandSparklesIcon,
                                tooltip: "Where each doc type's skill will plug in",
                                label: "Skills",
                                to: "/oura/skills",
                                roles: ["admin", "super_admin"],
                                group: "growth",
                            }]
                            : [])]
                        : []),
                ]);
            }

            if (userInfo.role === "job_seeker") {
                // Demo-practice mode strips the marketplace-style menu items
                // (Explore / My Jobs / Contract / Payment) so the sidebar
                // only surfaces the practice-interview + resume flow.
                const isDemoPracticeMode = import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true";
                const jobSeekerItems: NavItem[] = [
                    {
                        name: "job-matches",
                        icon: BriefcaseIcon,
                        label: "Explore",
                        to: "/dashboard",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "interview",
                        icon: Video,
                        label: "Interview",
                        to: "/interview",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "resume",
                        icon: FileTextIcon,
                        label: "Resume",
                        to: "/resume",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "my-jobs",
                        icon: CheckIcon,
                        label: "My Jobs",
                        to: "/my-jobs",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "contracts",
                        icon: FileIcon,
                        label: "Contract",
                        to: "/contracts",
                        roles: ["job_seeker"]
                    },
                    {
                        name: "payment",
                        icon: CreditCardIcon,
                        label: "Payment",
                        to: "/payment",
                        roles: ["job_seeker"]
                    },
                ];
                const HIDDEN_IN_DEMO = new Set(["job-matches", "my-jobs", "contracts", "payment"]);
                // In demo-practice mode, surface a "Browse" entry above
                // "Interview" so job_seekers have a public landing destination.
                const browseItem: NavItem = {
                    name: "browse",
                    icon: Globe,
                    label: "Browse",
                    to: "/browse",
                    roles: ["job_seeker"],
                };
                if (isDemoPracticeMode) {
                    const filtered = jobSeekerItems.filter((item) => !HIDDEN_IN_DEMO.has(item.name));
                    const interviewIdx = filtered.findIndex((item) => item.name === "interview");
                    const insertAt = interviewIdx >= 0 ? interviewIdx : 0;
                    filtered.splice(insertAt, 0, browseItem);
                    setNavItems(filtered);
                } else {
                    setNavItems(jobSeekerItems);
                }
            }
        }
    }, [userInfo])

    return (
        <SnackbarProvider>
            <div className={`${!props.isDisabledFlex ? "flex h-screen overflow-hidden" : "sm:flex sm:h-screen sm:overflow-hidden"} bg-white dark:bg-gray-950`}>
                <div className={cn(
                    "hidden sm:flex flex-col items-center h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800",
                    isAdmin && !sidebarMini ? "sm:w-64" : "sm:w-20",
                )}>
                    {isAdmin ? (
                        <AdminSidebar
                            navItems={navItems}
                            mini={sidebarMini}
                            onToggleMini={toggleSidebar}
                        />
                    ) : (
                        <MinimalSidebarV2
                            navItems={navItems}
                        />
                    )}
                </div>
                <div className="flex-1 flex flex-col overflow-hidden">
                    <Header />
                    <MaintenanceBanner />
                    <TrialUsageBanner />
                    <main className="flex-1 overflow-hidden sm:pb-0 pb-16">
                        {props.children}

                        <footer className="fixed bottom-0 left-0 w-screen border-t bg-white py-2 px-1 sm:hidden h-[71px]">
                            <div className="flex items-center justify-around space-x-2">
                                {navItems.map((item, index) => {
                                    if (!userInfo || item.roles.indexOf(userInfo.role || "") === -1) return;
                                    if (isAccountStartupScreening && ["jobs", "hires", "assessments"].includes(item.name)) return;
                                    // hide payment menu for hdbank referral code
                                    if (item.name === "payment" && import.meta.env.VITE_HDBANK_REFERRAL_CODE.split(",").includes(userInfo.referral_code)) return;
                                    if (item.name === "hires" && userInfo.permission === "member") return;
                                    if (item.name === "billing" && (userInfo.permission !== "owner")) return;

                                    const isActive = currentPage === item.name;
                                    const Icon = item.icon;
                                    return (
                                        <button key={index} className="relative flex-1 group flex aspect-square w-[54px] h-[54px] cursor-pointer flex-col items-center justify-center gap-1 text-black" onClick={() => {
                                            navigate(item.to as string)
                                            if (item.label === "Resume") {
                                                setShowDotResume(false);
                                                setCookie("__dot_resume_menu__", "true");
                                            }
                                        }}>
                                            <Tooltip
                                                key={index}
                                                content={item.tooltip || item.label}
                                                side="right"
                                                asChild
                                            >
                                                <div className="rounded-xl text-gray-900 transition-all duration-300">
                                                    <Icon className={`hover:scale-110 transition-all duration-300 h-6 w-6 shrink-0 mx-auto ${isActive ? "text-blue-600 hover:text-blue-600 transition-all duration-300" : ""}`} />
                                                </div>
                                            </Tooltip>
                                            <span className={`text-[10px] ${isActive ? "text-blue-600" : ""}`}>{item.label}</span>
                                            {(isShowDotResume && item.label === "Resume") && (
                                                <div className="absolute -top-[8px] -right-1">
                                                    <NotificationDot size="small" />
                                                </div>
                                            )}
                                        </button>
                                    )
                                })}
                            </div>
                        </footer>
                    </main>
                </div>
            </div>
        </SnackbarProvider >
    )
}